import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'

import { Alert, Modal } from 'antd'
import { useSelector } from 'react-redux'
import { isArray, isBoolean, isFunction } from 'lodash'

import {
  acceptInternshipRequest,
  addInternshipRequestFile,
  createInternshipRequest,
  createInternshipRequestPeriod,
  deleteInternshipRequest,
  deleteInternshipRequestPeriods,
  setInternshipRequestToOnHold,
  updateInternshipRequest,
  updateInternshipRequestPeriod,
  validateInternshipRequestBySchool
} from '../../../utils/api/internshipRequest'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import { getErrorBody, onError, onSuccess, resolvePromises, translateErrorCode } from '../../../utils/apiHelper'
import { RequestPeriod } from '../../../utils/entities/requestPeriod'
import IndividualInternshipRequestForm from './IndividualInternshipRequestForm'
import StudentForm from '../StudentForm/StudentForm'
import { Permissions } from '../StudentForm/SearchStudentModalProvider'
import { answerMatchesProposal, getPartnerProposal, getProposedPeriods, InternshipRequest } from '../../../utils/entities/internshipRequest'
import { getEntityOrNull, getIdOrNull, isObject } from '../../../utils'
import { isSchoolUser } from '../../../utils/roles'
import AcceptIndividualInternshipRequestForm from './AcceptIndividualInternshipRequestForm'
import useScreenSize from '../../../hooks/useScreen'
import useDebounce from '../../../hooks/useDebounce'
import { HttpStatuses } from '../../../utils/http'
import { createSchoolSection } from '../../../utils/api/school'
import RequestMetadataDisplayer from './RequestMetadataDisplayer'
import { ACCESS_KEY } from '../../../utils/api/internshipRequestPortal'

export const IndividualInternshipRequestModalContext = createContext()

/** @enum {string} */
export const ModalTypes = Object.freeze({
  FORM: 'FORM',
  DISPLAY_STUDENT: 'DISPLAY_STUDENT',
  DISPLAY_METADATA: 'DISPLAY_METADATA',
  ACCEPT: 'ACCEPT',
  INSTITUTION_VALIDATION: 'INSTITUTION_VALIDATION',
  SECTOR_VALIDATION: 'SECTOR_VALIDATION',
  STUDENT: 'STUDENT'
})

const DEFAULT_REQUEST = { data: null, callback: null, type: ModalTypes.FORM }

const PENDING_ANSWER_MESSAGES = Object.freeze({
  school: {
    opened: 'internship_request.actions.accept.waiting_for_institution',
    countered: 'internship_request.actions.accept.counter_offer.waiting_for_institution'
  },
  institution: {
    opened: 'internship_request.actions.accept.waiting_for_partner',
    countered: 'internship_request.actions.accept.counter_offer.waiting_for_partner'
  }
})

const ANSWER_ERROR_PREFIX = 'internship_request.actions.accept.error'
const SAVE_ERROR_PREFIX = 'internship_request.errors'

const joinFileNames = files => files.map(f => f.file.name).join(', ')

const IndividualInternshipRequestModal = ({ children, schools = { data: [] }, schoolSections }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)
  const { width } = useScreenSize()

  const [handler, setHandler] = useState(DEFAULT_REQUEST)
  const [selectedPeriods, setSelectedPeriods] = useState([])
  const [compensation, setCompensation] = useState(false)
  const [selectedSector, setSelectedSector] = useState(null)
  const [sectionHasToBeCreated, setSectionHasToBeCreated] = useState(false)
  const [saving, setSaving] = useState(false)

  const displayForm = useMemo(() => !!handler.data && handler.type === ModalTypes.FORM, [handler])
  const displayStudentData = useMemo(() => !!handler.data && handler.type === ModalTypes.DISPLAY_STUDENT, [handler])
  const displayAcceptModal = useMemo(() => !!handler.data && handler.type === ModalTypes.ACCEPT, [handler])
  const displayMetadata = useMemo(() => !!handler.data && handler.type === ModalTypes.DISPLAY_METADATA, [handler])

  const answeringAsSchool = isSchoolUser(user)
  const dualApprovalRequired = handler.data?.dualApprovalRequired === true
  const schoolHalf = answeringAsSchool && dualApprovalRequired
  const partnerProposal = useMemo(
    () => displayAcceptModal ? getPartnerProposal(handler.data, answeringAsSchool) : null,
    [displayAcceptModal, handler.data, answeringAsSchool]
  )
  const answerAgrees = useMemo(
    () => answerMatchesProposal(partnerProposal, selectedPeriods, selectedSector),
    [partnerProposal, selectedPeriods, selectedSector]
  )

  useEffect(() => {
    if (!displayAcceptModal) {
      return
    }

    const proposal = getPartnerProposal(handler.data, answeringAsSchool)

    setCompensation(proposal?.compensation === true)
    setSelectedPeriods(getProposedPeriods(handler.data, proposal).map(p => p.id))
    setSelectedSector(getEntityOrNull(proposal?.sector) ?? getEntityOrNull(handler.data.sector))
    setSectionHasToBeCreated(false)
  }, [displayAcceptModal, handler.data?.id, answeringAsSchool])

  const closeModal = useCallback(() => {
    setHandler(DEFAULT_REQUEST)
    setSelectedPeriods([])
    setCompensation(false)
    setSelectedSector(null)
    setSectionHasToBeCreated(false)
  }, [])

  const submit = useCallback((request, files, mandatoryFileTypes = []) => {
    const promise = request.id < 0 ? { call: createInternshipRequest, action: 'create' } : { call: updateInternshipRequest, action: 'update' }
    const oldRequest = handler.data
    const requestPeriodsToCreate = []
    const requestPeriodsToUpdate = []
    const requestPeriodsToDelete = []

    if (promise.action === 'update') {
      request.requestPeriods.forEach(p => {
        if (p.id < 0) {
          requestPeriodsToCreate.push(new RequestPeriod(p))
        } else {
          const oldPeriod = oldRequest.requestPeriods.find(rp => rp.id === p.id)

          if (oldPeriod && (oldPeriod.startDate !== p.startDate || oldPeriod.endDate !== p.endDate)) {
            requestPeriodsToUpdate.push(new RequestPeriod(p))
          }
        }
      })

      oldRequest.requestPeriods.forEach(p => {
        if (!request.requestPeriods.find(rp => rp.id === p.id)) {
          requestPeriodsToDelete.push(p)
        }
      })
      request.requestPeriods = []
    }

    const body = request.toRequestBody()

    if (handler.portalAccessKey) {
      body[ACCESS_KEY] = handler.portalAccessKey
    }

    setSaving(true)

    promise.call(user, body).then(json => {
      if (!json?.data) {
        return false
      }

      const promisesStack = []

      requestPeriodsToCreate.forEach(rp => {
        promisesStack.push(createInternshipRequestPeriod(user, oldRequest, rp.toRequestBody()))
      })
      requestPeriodsToUpdate.forEach(rp => {
        promisesStack.push(updateInternshipRequestPeriod(user, oldRequest, rp.toRequestBody()))
      })

      if (requestPeriodsToDelete.length > 0) {
        promisesStack.push(deleteInternshipRequestPeriods(user, oldRequest, { ids: requestPeriodsToDelete.map(rp => rp.id) }))
      }

      const uploads = isArray(files)
        ? files.map(f => addInternshipRequestFile(user, json.data, f).then(() => null, () => f))
        : []

      const settle = (failedFiles, failedPeriods) => {
        if (failedFiles.length > 0) {
          onError(t('internship_request.' + promise.action + '.files_error', { files: joinFileNames(failedFiles) }))
        }

        if (failedPeriods) {
          onError(t('internship_request.' + promise.action + '.periods_error'))
        }

        if (failedFiles.length === 0 && !failedPeriods) {
          onSuccess(t('internship_request.' + promise.action + '.success'))
        }

        if (handler.callback) {
          handler.callback(json.data)
        }

        return false
      }

      return resolvePromises([Promise.allSettled(promisesStack), resolvePromises(uploads)]).then(([periods, uploaded]) => {
        const failedPeriods = periods.some(r => r.status === 'rejected')
        const failedFiles = uploaded.filter(isObject)
        const failedMandatoryFiles = failedFiles.filter(f => mandatoryFileTypes.includes(f.type))

        if (promise.action !== 'create' || failedMandatoryFiles.length === 0) {
          return settle(failedFiles, failedPeriods)
        }

        return deleteInternshipRequest(user, json.data).then(() => {
          onError(t('internship_request.create.mandatory_files_error', { files: joinFileNames(failedMandatoryFiles) }))

          return true
        }, () => settle(failedFiles, failedPeriods))
      })
    }).catch(error => {
      const fallbackKey = 'internship_request.' + promise.action + '.error'

      if (typeof error?.body?.getReader !== 'function') {
        onError(t(fallbackKey))

        return
      }

      getErrorBody(error)
        .then(({ message }) => onError(translateErrorCode(t, message, SAVE_ERROR_PREFIX, fallbackKey)))
        .catch(() => onError(t(fallbackKey)))
    }).then(keepForm => {
      setSaving(false)

      if (!keepForm) {
        setHandler(DEFAULT_REQUEST)
      }
    })
  }, [handler, user, t])

  const handleSubmit = useDebounce(submit, 200)

  const setRequestAsPending = useCallback(() => {
    setInternshipRequestToOnHold(user, handler.data).then(json => {
      if (json?.data) {
        if (isFunction(handler.callback)) {
          handler.callback(json.data)
        }

        onSuccess(t('internship_request.set_pending.success'))
        setHandler(DEFAULT_REQUEST)
      } else {
        onError(t('internship_request.set_pending.error'))
      }
    })
  }, [handler, user, setHandler, t])

  const acceptRequest = useCallback(() => {
    const answeredAProposal = partnerProposal !== null
    const answeredRequest = { ...handler.data, sector: selectedSector }
    const settlesTheRequest = !dualApprovalRequired || (answeredAProposal && answerAgrees)
    const answer = schoolHalf
      ? validateInternshipRequestBySchool(user, answeredRequest, selectedPeriods)
      : acceptInternshipRequest(user, answeredRequest, selectedPeriods, compensation)

    answer.then(response => {
      const { data, meta } = response ?? {}

      if (isFunction(handler.callback)) {
        handler.callback(data)
      }

      const internshipsCreated = isBoolean(meta?.internshipsCreated) ? meta.internshipsCreated : settlesTheRequest

      onSuccess(t(internshipsCreated
        ? 'internship_request.actions.accept.success'
        : PENDING_ANSWER_MESSAGES[schoolHalf ? 'school' : 'institution'][answeredAProposal ? 'countered' : 'opened']
      ))
      closeModal()
    }).catch(error => {
      if (error.status !== HttpStatuses.BAD_REQUEST) {
        onError(t(ANSWER_ERROR_PREFIX))

        return
      }

      getErrorBody(error).then(({ message }) => {
        if (message === 'SCHOOL_SECTION_MUST_BE_CREATED') {
          setSectionHasToBeCreated(true)
        } else {
          onError(translateErrorCode(t, message, ANSWER_ERROR_PREFIX, ANSWER_ERROR_PREFIX))
        }
      }).catch(() => onError(t(ANSWER_ERROR_PREFIX)))
    })
  }, [user, handler, selectedPeriods, selectedSector, compensation, schoolHalf, dualApprovalRequired, partnerProposal, answerAgrees, t, closeModal])

  const createSection = useCallback(() => {
    createSchoolSection(user, handler.data.school, handler.data.sectionToYear.section).then(() => {
      onSuccess(t('internship_request.actions.section_creation.success'))
      acceptRequest()
    }).catch(() => {
      onError(t('internship_request.actions.section_creation.error'))
    })
  }, [acceptRequest])

  const student = useMemo(() => handler.data ? new InternshipRequest(handler.data).getStudent() : null, [handler.data])

  const answerButtonLabel = useMemo(() => {
    if (sectionHasToBeCreated) {
      return 'internship_request_form.acceptation_button.create_section'
    }

    if (partnerProposal === null) {
      return 'student_information_modal.accept_internship_request_button'
    }

    return answerAgrees
      ? 'accept_internship_request_modal.partner_proposal.confirm'
      : 'accept_internship_request_modal.partner_proposal.counter'
  }, [sectionHasToBeCreated, partnerProposal, answerAgrees])

  return (
    <>
      <IndividualInternshipRequestModalContext.Provider value={{ setHandler: setHandler }}>
        {children}
      </IndividualInternshipRequestModalContext.Provider>
      <Modal
        width={width > 1024 ? 1024 : '90%'}
        title={t('internship_request_modal.title')}
        visible={displayForm}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
      >
        <IndividualInternshipRequestForm
          data={displayForm ? handler.data : null}
          schools={schools}
          schoolSections={schoolSections}
          saving={saving}
          onSubmit={handleSubmit}
        />
      </Modal>
      <Modal
        title={t('student_information_modal.title')}
        visible={displayStudentData}
        onCancel={closeModal}
        onOk={setRequestAsPending}
        okText={t('student_information_modal.ok_button')}
        cancelText={t('student_information_modal.cancel_button')}
      >
        {displayStudentData && (
          <StudentForm
            data={student}
            permission={Permissions.READ_ONLY}
          />
        )}
      </Modal>
      <Modal
        title={t('metadata_modal.title')}
        visible={displayMetadata}
        onCancel={closeModal}
        onOk={setRequestAsPending}
        okText={t('metadata_modal.ok_button')}
        cancelText={t('metadata_modal.cancel_button')}
      >
        {displayMetadata && <RequestMetadataDisplayer metadata={handler.data.metadata} />}
      </Modal>
      <Modal
        title={t(partnerProposal !== null ? 'accept_internship_request_modal.partner_proposal.title' : 'accept_internship_request_modal.title')}
        visible={displayAcceptModal}
        onCancel={closeModal}
        onOk={sectionHasToBeCreated ? createSection : acceptRequest}
        okText={t(answerButtonLabel)}
        okButtonProps={{ disabled: selectedPeriods.length === 0 || getIdOrNull(selectedSector) === null }}
        cancelText={t(sectionHasToBeCreated ? 'common.wait' : 'student_information_modal.cancel_button')}
      >
        {displayAcceptModal && !sectionHasToBeCreated && (
          <AcceptIndividualInternshipRequestForm
            student={student}
            request={handler.data}
            sector={selectedSector}
            compensation={compensation}
            proposal={partnerProposal}
            agrees={answerAgrees}
            selectedPeriods={selectedPeriods}
            onCompensationChange={setCompensation}
            onSectorSelect={setSelectedSector}
            onPeriodsSelect={setSelectedPeriods}
          />
        )}
        {displayAcceptModal && sectionHasToBeCreated && (
          <Alert
            message={t('internship_request_form.info.unsupported_section')}
            description={t('internship_request_form.info.unsupported_section_description')}
            type='info'
            showIcon
          />
        )}
      </Modal>
    </>
  )
}

export default IndividualInternshipRequestModal
